import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Divider, Input, Select, Space, Tag } from 'antd';
import { CustomButton, CustomShipmentText, SearchInput } from '@/components';
import {
  AddCircleOutlineSmallIcon,
  ChevronDownSmallIcon,
  MenuCloseIcon,
  MenuSearchIcon,
} from '@/assets/icons/icons';
import { CustomVariableTag } from './CustomVariableTag';
import type { CreateCustomVariableInput } from './AddCustomVariablePanel';
import type { CustomVariableTypeName } from '@/common/collectVariableType';
import { CustomDeleteModal } from '@/pages/Modals';
import './CheckboxMultiSelect.css';

// Filled black circle with a white X — the "clear" affordance on the
// inline quick-add name field (Figma node 39150:196413).
const ClearCircleIcon: React.FC = () => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 12 12'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle cx='6' cy='6' r='6' fill='var(--text-primary)' />
    <path
      d='M4 4L8 8M8 4L4 8'
      stroke='var(--white)'
      strokeWidth='1'
      strokeLinecap='round'
    />
  </svg>
);

// Thrown by onDeleteCustomOption to indicate the variable can't be deleted
// because it's still referenced by an agent's collect list.
export const CUSTOM_VAR_IN_USE_ERROR = 'CUSTOM_VAR_IN_USE';

export type CheckboxMultiSelectOption = {
  disabled?: boolean;
  value: string | number;
  label: string;
  // Overrides the selected-pill/tag text when it should differ from the
  // dropdown row label — e.g. a row shown as "Resolved" under a "Manual
  // actions" group heading, but rendered as "Manual resolved" once it's a
  // standalone pill that's lost that grouping context. Falls back to
  // `label` when omitted.
  tagLabel?: string;
  type?: CustomVariableTypeName;
  isCustomVar?: boolean;
  description?: string;
  sample_value?: string;
  is_in_use?: boolean;
  // Not yet persisted server-side - removing it is a local-only operation,
  // see handleConfirmDeleteCustomOption below.
  isPending?: boolean;
};

// A labeled section of options, rendered under its own subtitle row inside
// the dropdown. Used by the `groups` prop for a caller-defined arbitrary
// number of named sections (as opposed to `groupCustomVariables`'s fixed
// two-bucket custom/default split).
export type CheckboxMultiSelectGroup = {
  label: string;
  options: CheckboxMultiSelectOption[];
};

// A leading checkbox row that selects a caller-defined subset of options
// spanning several groups at once — e.g. "All resolved" covering both the
// "Manual actions" and "AI actions" Resolved rows. Unlike `selectAllLabel`,
// selecting one still shows each covered option as its own tag.
export type CheckboxMultiSelectShortcut = {
  label: string;
  values: Array<string | number>;
};

// Case-insensitive filter of options by label. Empty query returns all options.
export const filterOptionsByLabel = (
  options: CheckboxMultiSelectOption[],
  query: string
): CheckboxMultiSelectOption[] => {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.label.toLowerCase().includes(q));
};

export interface CheckboxMultiSelectProps {
  // Required unless `groups` is provided, in which case it's ignored — the
  // effective option list is derived by flattening `groups` instead.
  options?: CheckboxMultiSelectOption[];
  value?: Array<string | number>;
  onChange?: (next: Array<string | number>) => void;

  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;

  titleText?: string;
  className?: string;

  background?: string;
  border?: string;
  color?: string;

  isParernt?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showTagCloseIcon?: boolean;
  tagCloseMode?: 'instant' | 'stage';
  allowAllTagClose?: boolean;

  // Splits the dropdown list into a "Custom variables" group (options with
  // isCustomVar: true) above a "Default variables" group (everything else).
  groupCustomVariables?: boolean;

  // Renders the dropdown as N caller-labeled sections instead of a flat
  // list. Takes priority over `options`/`groupCustomVariables` when set —
  // the effective option list is derived by flattening these groups, so
  // `options` itself is ignored. Applies each check/uncheck immediately
  // (no Reset/Apply footer), same as `groupCustomVariables`.
  groups?: CheckboxMultiSelectGroup[];
  stagedGroups?: boolean;

  // When set alongside `groups`, renders a leading checkbox row above the
  // groups labeled with this text. Checked when every option across all
  // groups is selected; toggling it selects/deselects everything. In the
  // flat Reset/Apply list it selects/deselects the options the search shows,
  // staged until Apply.
  selectAllLabel?: string;

  // When set alongside `groups`, renders one checkbox row per shortcut
  // below the `selectAllLabel` row. Checked when every one of its values is
  // selected; toggling it adds/removes just those values, leaving the rest
  // of the selection untouched.
  shortcuts?: CheckboxMultiSelectShortcut[];

  // Where the dropdown is mounted; passed straight through to antd Select.
  // Defaults to document.body. Mount it inside a scrollable panel to have
  // scrolling past the end of the list carry on into that panel instead of
  // the page.
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;

  // Called when the close ("X") icon on a custom variable row is clicked and
  // the user confirms. The caller performs the actual server-side delete;
  // rejecting with an Error whose message is CUSTOM_VAR_IN_USE_ERROR shows
  // the "unable to delete" dialog instead of a generic failure.
  onDeleteCustomOption?: (value: string | number) => Promise<void>;

  // Called when "Add custom variable" is saved. The caller is responsible
  // for actually creating it server-side; the returned promise rejecting
  // keeps the panel open so the user can retry. Resolving with the new
  // option's value auto-selects it; resolving with nothing leaves it unselected.
  onCreateCustomOption?: (
    input: CreateCustomVariableInput
  ) => Promise<string | number | void>;

  // Called when a custom variable's edit popover is saved. The caller
  // persists the new name/type/example/description and updates its own
  // option list. A rejected promise keeps the popover open so the user can
  // retry instead of silently losing the edit.
  onUpdateCustomOption?: (
    value: string | number,
    updated: CreateCustomVariableInput
  ) => void | Promise<void>;
}

const CheckboxMultiSelect: React.FC<CheckboxMultiSelectProps> = ({
  options = [],
  value = [],
  onChange,

  open: controlledOpen,
  setOpen: controlledSetOpen,

  titleText,
  className = 'custom-dd-style',

  background,
  border,
  color,

  isParernt,
  placeholder = 'Select...',
  disabled = false,

  showTagCloseIcon = false,
  tagCloseMode = 'instant',
  allowAllTagClose,
  groupCustomVariables = false,
  groups,
  stagedGroups = false,
  selectAllLabel,
  shortcuts,
  getPopupContainer,
  onDeleteCustomOption,
  onCreateCustomOption,
  onUpdateCustomOption,
}) => {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const [selectHovered, setSelectHovered] = useState(false);
  const [hiddenCustomValues, setHiddenCustomValues] = useState<
    Set<string | number>
  >(new Set());
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [pendingDeleteValue, setPendingDeleteValue] = useState<
    string | number | null
  >(null);
  const [deleteBlockedOpen, setDeleteBlockedOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledSetOpen ?? setInternalOpen;

  const effectiveOptions = useMemo<CheckboxMultiSelectOption[]>(
    () => (groups ? groups.flatMap((g) => g.options) : options),
    [groups, options]
  );

  const uniqueList = useMemo<CheckboxMultiSelectOption[]>(() => {
    if (!effectiveOptions?.length) return [];
    return effectiveOptions.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
    );
  }, [effectiveOptions]);

  const valueToLabel = useMemo(() => {
    const m = new Map<string | number, string>();
    uniqueList.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [uniqueList]);

  const valueToOption = useMemo(() => {
    const m = new Map<string | number, CheckboxMultiSelectOption>();
    uniqueList.forEach((o) => m.set(o.value, o));
    return m;
  }, [uniqueList]);

  const allIds = useMemo<Array<string | number>>(
    () => uniqueList.filter((option) => !option.disabled).map((x) => x.value),
    [uniqueList]
  );

  const [search, setSearch] = useState<string>('');

  const filteredList = useMemo<CheckboxMultiSelectOption[]>(
    () => filterOptionsByLabel(uniqueList, search),
    [uniqueList, search]
  );

  const customVarList = useMemo<CheckboxMultiSelectOption[]>(
    () =>
      filteredList.filter(
        (o) => o.isCustomVar && !hiddenCustomValues.has(o.value)
      ),
    [filteredList, hiddenCustomValues]
  );

  const defaultVarList = useMemo<CheckboxMultiSelectOption[]>(
    () => filteredList.filter((o) => !o.isCustomVar),
    [filteredList]
  );

  const [selectedOption, setSelectedOption] = useState<Array<string | number>>(
    []
  );
  const [appliedOptions, setAppliedOptions] = useState<Array<string | number>>(
    []
  );

  useEffect(() => {
    setAppliedOptions(value || []);
    setSelectedOption(value || []);
  }, [value]);

  const handleReset = () => {
    setSelectedOption([]);
    setAppliedOptions([]);
    onChange?.([]);
    setOpen(false);
  };

  const handleDropdownVisibleChange = (visible: boolean) => {
    if (visible) setSelectedOption(appliedOptions);
    else {
      setSearch('');
      setAddPanelOpen(false);
      setQuickAddName('');
    }
    setOpen(visible);
  };

  const handleApply = () => {
    setAppliedOptions(selectedOption);
    onChange?.(selectedOption);
    setOpen(false);
  };

  const appliedIsAllSelected =
    appliedOptions.length > 0 && appliedOptions.length === uniqueList.length;

  const tagText = (val: string | number) =>
    valueToOption.get(val)?.tagLabel || valueToLabel.get(val) || String(val);

  const toggleOne = (v: string | number) => {
    if (valueToOption.get(v)?.disabled) return;
    setSelectedOption((prev) => {
      const next = prev.includes(v)
        ? prev.filter((x) => x !== v)
        : [...prev, v];
      // The grouped custom/default variable list (and the labeled `groups`
      // list) applies each check/uncheck immediately so the tag display
      // stays in sync while the dropdown is open.
      if (groupCustomVariables || (groups && !stagedGroups)) {
        setAppliedOptions(next);
        onChange?.(next);
      }
      return next;
    });
  };

  // Selects every option across all groups, or clears the selection if
  // everything is already selected. Immediate-apply, same as toggleOne.
  const toggleAll = () => {
    const next = selectedOption.length === allIds.length ? [] : allIds;
    setSelectedOption(next);
    setAppliedOptions(next);
    onChange?.(next);
  };

  // Adds a shortcut's values to the selection, or removes them if they're
  // all already selected. Immediate-apply, same as toggleOne.
  const toggleShortcut = (shortcutValues: Array<string | number>) => {
    const allIn = shortcutValues.every((v) => selectedOption.includes(v));
    const next = allIn
      ? selectedOption.filter((v) => !shortcutValues.includes(v))
      : [
          ...selectedOption,
          ...shortcutValues.filter((v) => !selectedOption.includes(v)),
        ];
    setSelectedOption(next);
    setAppliedOptions(next);
    onChange?.(next);
  };

  // Flat list: (de)selects what the search shows, staged until Apply.
  const toggleShown = () => {
    const shown = filteredList
      .filter((option) => !option.disabled)
      .map((o) => o.value);
    setSelectedOption((prev) =>
      shown.every((v) => prev.includes(v))
        ? prev.filter((v) => !shown.includes(v))
        : [...prev, ...shown.filter((v) => !prev.includes(v))]
    );
  };

  const removeCustomOption = (v: string | number) => {
    setHiddenCustomValues((prev) => new Set(prev).add(v));
    setSelectedOption((prev) => prev.filter((x) => x !== v));
  };

  const handleQuickCreateSave = async () => {
    const name = quickAddName.trim();
    if (!name || !onCreateCustomOption || quickAddSaving) return;
    setQuickAddSaving(true);
    try {
      const input: CreateCustomVariableInput = {
        name,
        type: 'TEXT',
        description: '',
        sample_value: '',
      };
      const createdValue = await onCreateCustomOption(input);
      setQuickAddName('');
      setAddPanelOpen(false);
      if (
        typeof createdValue === 'string' ||
        typeof createdValue === 'number'
      ) {
        setSelectedOption((prev) =>
          prev.includes(createdValue) ? prev : [...prev, createdValue]
        );
        setAppliedOptions((prev) => {
          const next = prev.includes(createdValue)
            ? prev
            : [...prev, createdValue];
          onChange?.(next);
          return next;
        });
      }
    } catch {
      // parent surfaces the error via toast; keep the field open so the user can retry
    } finally {
      setQuickAddSaving(false);
    }
  };

  const removeOneFromApplied = (v: string | number) => {
    const next = appliedOptions.filter((x) => x !== v);
    setAppliedOptions(next);
    setSelectedOption(next);
    onChange?.(next);
  };

  const removeOneFromSelectedOnly = (v: string | number) => {
    setSelectedOption((prev) => prev.filter((x) => x !== v));
  };

  const computedAllowAllTagClose = allowAllTagClose ?? !isParernt;

  const handleConfirmDeleteCustomOption = async () => {
    if (pendingDeleteValue === null) return;
    const value = pendingDeleteValue;

    // Never persisted server-side, so there's nothing to delete remotely -
    // just drop it from the local list.
    const option = options.find((o) => o.value === value);
    if (option?.isPending) {
      removeCustomOption(value);
      setPendingDeleteValue(null);
      return;
    }

    if (!onDeleteCustomOption) return;
    try {
      await onDeleteCustomOption(value);
      removeCustomOption(value);
      setPendingDeleteValue(null);
    } catch (err) {
      setPendingDeleteValue(null);
      if (err instanceof Error && err.message === CUSTOM_VAR_IN_USE_ERROR) {
        setDeleteBlockedOpen(true);
      }
    }
  };

  return (
    <>
      <CustomDeleteModal
        visible={pendingDeleteValue !== null}
        onClose={() => setPendingDeleteValue(null)}
        onConfirm={handleConfirmDeleteCustomOption}
        title='Delete custom variable'
        message='This variable will be deleted for all users. This action cannot be undone.'
        cancelText='Cancel'
        confirmText='Delete variable'
        confirmColor='var(--text-primary)'
        messageFontSize={16}
        titleGap='20px'
        actionsGap={16}
      />
      <CustomDeleteModal
        visible={deleteBlockedOpen}
        onClose={() => setDeleteBlockedOpen(false)}
        onConfirm={() => setDeleteBlockedOpen(false)}
        title='Unable to delete custom variable'
        message='This custom variable cannot be deleted as it is used in agent instructions.'
        cancelText='Cancel'
        confirmText='Got it'
        confirmColor='var(--text-primary)'
        messageFontSize={16}
        titleGap='20px'
        actionsGap={16}
      />
      <div
        className='ivocSelectWrap'
        onMouseEnter={() => setSelectHovered(true)}
        onMouseLeave={() => setSelectHovered(false)}
      >
        <Select<Array<string | number>>
          mode='multiple'
          value={appliedOptions}
          open={open}
          getPopupContainer={getPopupContainer}
          disabled={disabled}
          onOpenChange={handleDropdownVisibleChange}
          placeholder={placeholder}
          className={className}
          showSearch={false}
          suffixIcon={
            // AntD lays the suffix icon out as a fixed-width flex sibling
            // of the tag/search area (not an absolutely-positioned
            // overlay) - its rendered width directly changes how much
            // width is left for tags to wrap into. The hover "clear all"
            // icon was wider than the plain chevron, so swapping to it on
            // hover shrank that width just enough to push the always-
            // present (but invisible) search input onto its own wrapped
            // line, showing as a blank row. Locking both variants to the
            // same 16x16 footprint (the chevron's own bare size) means
            // hovering never changes that width.
            <span className='ivocSuffixIconBox'>
              {(groupCustomVariables || groups) &&
              selectHovered &&
              !disabled &&
              appliedOptions.length > 0 ? (
                <span
                  className='ivocResetAll'
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReset();
                  }}
                >
                  <svg
                    width='10'
                    height='10'
                    viewBox='0 0 10 10'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M1.5 1.5L8.5 8.5M8.5 1.5L1.5 8.5'
                      stroke='var(--text-primary)'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                    />
                  </svg>
                </span>
              ) : (
                <span
                  className={
                    open ? 'ivocChevron ivocChevronOpen' : 'ivocChevron'
                  }
                >
                  <ChevronDownSmallIcon
                    size={16}
                    color='var(--text-secondary)'
                  />
                </span>
              )}
            </span>
          }
          tagRender={({ value: tagValue }) => {
            const onPreventMouseDown = (
              event: React.MouseEvent<HTMLSpanElement>
            ) => {
              event.preventDefault();
              event.stopPropagation();
            };

            const isDisabled = !!disabled;

            if (!appliedOptions.length) return <></>;

            if (appliedIsAllSelected) {
              if (tagValue !== allIds[0]) return <></>;

              return (
                <Tag
                  className='notification-filter-tag'
                  closable={showTagCloseIcon && computedAllowAllTagClose}
                  onMouseDown={onPreventMouseDown}
                  onClose={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isDisabled) return;
                    handleReset();
                  }}
                  style={{
                    background: background || 'var(--surface-container)',
                    color: color || 'var(--text-secondary)',
                    border: border || '1px solid var(--gray-300)',
                    cursor: isDisabled ? 'not-allowed' : 'default',
                    opacity: isDisabled ? 0.7 : 1,
                    margin: '0 4px 0 0',
                  }}
                >
                  <span>All</span>
                </Tag>
              );
            }

            const canClose = showTagCloseIcon && !isParernt;

            const option = valueToOption.get(tagValue as string | number);
            if (option?.isCustomVar) {
              return (
                <span onMouseDown={onPreventMouseDown} className='ivocTagWrap'>
                  <CustomVariableTag
                    name={option.label}
                    type={option.type || 'TEXT'}
                    example={option.sample_value}
                    description={option.description}
                    isPending={option.isPending}
                    onSave={
                      onUpdateCustomOption
                        ? (updated) =>
                            onUpdateCustomOption(tagValue, {
                              name: updated.name,
                              type: updated.type,
                              description: updated.description,
                              sample_value: updated.example,
                            })
                        : undefined
                    }
                    onRemove={
                      canClose && !isDisabled
                        ? () => {
                            if (tagCloseMode === 'instant') {
                              removeOneFromApplied(tagValue as string | number);
                            } else {
                              removeOneFromSelectedOnly(
                                tagValue as string | number
                              );
                            }
                          }
                        : undefined
                    }
                  />
                </span>
              );
            }

            return (
              <Tag
                className='notification-filter-tag'
                closable={canClose}
                onMouseDown={onPreventMouseDown}
                onClose={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (!canClose) return;
                  if (isDisabled) return;

                  if (tagCloseMode === 'instant') {
                    removeOneFromApplied(tagValue as string | number);
                  } else {
                    removeOneFromSelectedOnly(tagValue as string | number);
                  }
                }}
                style={{
                  background: background || 'var(--surface-container)',
                  color: color || 'var(--text-secondary)',
                  border: border || '1px solid var(--gray-300)',
                  cursor: isDisabled ? 'not-allowed' : 'default',
                  opacity: isDisabled ? 0.7 : 1,
                  margin: '0 4px 0 0',
                }}
              >
                <span>{tagText(tagValue as string | number)}</span>
              </Tag>
            );
          }}
          dropdownRender={() => {
            const renderRow = (item: CheckboxMultiSelectOption) => (
              <div
                key={String(item.value)}
                className={`ivocRow${item.disabled ? ' ivocRow--disabled' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  className='ivocRow__checkbox'
                  disabled={item.disabled}
                  checked={selectedOption.includes(item.value)}
                  onChange={() => toggleOne(item.value)}
                >
                  {item.label}
                </Checkbox>
                {item.isCustomVar && (
                  <span
                    className='ivocRow__close'
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.is_in_use) {
                        setDeleteBlockedOpen(true);
                      } else {
                        setPendingDeleteValue(item.value);
                      }
                    }}
                  >
                    <MenuCloseIcon />
                  </span>
                )}
              </div>
            );

            if (groups) {
              const allSelected =
                allIds.length > 0 && selectedOption.length === allIds.length;
              return (
                // One scroll area for the whole list, so the select-all and
                // shortcut rows scroll away with the groups instead of
                // staying pinned above them.
                <div className='ivocDropdown'>
                  <div className='ivocScrollArea'>
                    {selectAllLabel && (
                      <div
                        key='__select_all'
                        className='ivocRow'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          className='ivocRow__checkbox'
                          checked={allSelected}
                          onChange={toggleAll}
                        >
                          {selectAllLabel}
                        </Checkbox>
                      </div>
                    )}
                    {shortcuts?.map((shortcut) => (
                      <div
                        key={`__shortcut_${shortcut.label}`}
                        className='ivocRow'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          className='ivocRow__checkbox'
                          checked={
                            shortcut.values.length > 0 &&
                            shortcut.values.every((v) =>
                              selectedOption.includes(v)
                            )
                          }
                          onChange={() => toggleShortcut(shortcut.values)}
                        >
                          {shortcut.label}
                        </Checkbox>
                      </div>
                    ))}
                    {(!!selectAllLabel || !!shortcuts?.length) && (
                      <div className='ivocDivider' />
                    )}
                    {groups.map((group, index) => (
                      <div key={group.label}>
                        {index > 0 && <div className='ivocDivider' />}
                        <div className='ivocSubtitle'>{group.label}</div>
                        {group.options.map(renderRow)}
                      </div>
                    ))}
                  </div>
                  {stagedGroups && (
                    <Space
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        padding: 8,
                      }}
                    >
                      <CustomButton
                        text='Reset'
                        onClick={handleReset}
                        width='auto'
                        height='32px'
                        bgColor='transparent'
                        color='var(--text-primary)'
                        fontSize='12px'
                        boxShadow='none'
                      />
                      <CustomButton
                        text='Apply'
                        onClick={handleApply}
                        width='62px'
                        height='32px'
                        bgColor='#000'
                        color='#fff'
                        fontSize='12px'
                        borderRadius='8px'
                      />
                    </Space>
                  )}
                </div>
              );
            }

            if (groupCustomVariables) {
              return (
                <div className='ivocDropdown'>
                  <div className='ivocSearchRow'>
                    <div className='ivocSearchPill'>
                      <span className='ivocSearchIcon'>
                        <MenuSearchIcon />
                      </span>
                      <input
                        className='ivocSearchInput'
                        placeholder='Search...'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  <div className='ivocDivider' />

                  <div className='ivocScrollArea'>
                    <div className='ivocSubtitle'>Custom variables</div>
                    {customVarList.length === 0 ? (
                      <div className='ivocEmpty'>No results</div>
                    ) : (
                      customVarList.map(renderRow)
                    )}

                    <div className='ivocDivider' />

                    <div className='ivocSubtitle'>Default variables</div>
                    {defaultVarList.length === 0 ? (
                      <div className='ivocEmpty'>No results</div>
                    ) : (
                      defaultVarList.map(renderRow)
                    )}
                  </div>

                  <div className='ivocFooter'>
                    <div className='ivocAddCustomRow'>
                      {addPanelOpen ? (
                        <Input
                          autoFocus
                          className='ivocQuickAddInput'
                          placeholder='Enter variable name'
                          value={quickAddName}
                          disabled={quickAddSaving}
                          allowClear={{ clearIcon: <ClearCircleIcon /> }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setQuickAddName(e.target.value)}
                          onPressEnter={handleQuickCreateSave}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setQuickAddName('');
                              setAddPanelOpen(false);
                            }
                          }}
                          onBlur={() => {
                            if (!quickAddName.trim()) setAddPanelOpen(false);
                          }}
                        />
                      ) : (
                        <button
                          type='button'
                          className='ivocAddCustomBtn'
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddPanelOpen(true);
                          }}
                        >
                          <AddCircleOutlineSmallIcon />
                          <span>Add custom variable</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <>
                {titleText && (
                  <CustomShipmentText
                    text={titleText}
                    color='var(--text-secondary)'
                    fontWeight={600}
                    style={{ margin: '8px 16px 4px' }}
                  />
                )}

                <div className='ivocSearchWrap'>
                  <SearchInput
                    placeholder='Search...'
                    width='100%'
                    defaultValue={search}
                    onChange={(val) => setSearch(val)}
                    onClear={() => setSearch('')}
                  />
                </div>

                <div className='ivocFlatList'>
                  {selectAllLabel && filteredList.length > 0 && (
                    <div
                      className='notification-filter-option ivocOptionRow'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        className='notification-filter-checkbox notification-filter-checkbox--noMargin'
                        checked={filteredList.every((o) =>
                          selectedOption.includes(o.value)
                        )}
                        onChange={() => toggleShown()}
                      >
                        {selectAllLabel}
                      </Checkbox>
                    </div>
                  )}
                  {filteredList.length === 0 ? (
                    <div
                      className='ivocNoResults'
                      onClick={(e) => e.stopPropagation()}
                    >
                      No results
                    </div>
                  ) : (
                    filteredList.map((item) => (
                      <div
                        key={String(item.value)}
                        className={`notification-filter-option ivocOptionRow${item.disabled ? ' ivocRow--disabled' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          className='notification-filter-checkbox notification-filter-checkbox--noMargin'
                          disabled={item.disabled}
                          checked={selectedOption.includes(item.value)}
                          onChange={() => toggleOne(item.value)}
                        >
                          {item.label}
                        </Checkbox>
                      </div>
                    ))
                  )}
                </div>

                <Divider
                  className='notification-divider'
                  style={{ margin: '0' }}
                />

                <Space
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '8px 16px 4px',
                  }}
                >
                  <CustomButton
                    text='Reset'
                    onClick={handleReset}
                    width='auto'
                    height='32px'
                    bgColor='transparent'
                    color='var(--text-primary)'
                    fontSize='12px'
                    fontWeight={600}
                    borderRadius='0px'
                    boxShadow='none'
                  />
                  <CustomButton
                    text='Apply'
                    onClick={handleApply}
                    width='62px'
                    height='32px'
                    bgColor='#000'
                    color='#fff'
                    fontSize='12px'
                    fontWeight={500}
                    borderRadius='8px'
                    boxShadow='0px 4px 16px 0px rgba(0, 0, 0, 0.27)'
                  />
                </Space>
              </>
            );
          }}
        />
      </div>
    </>
  );
};

export default CheckboxMultiSelect;
